import { questionService } from '@/services/questionsApi'
import { groupsApi } from '@/services/groupsApi'

const state = {
  currentEvaluation: null,
  questions: [],
  currentQuestionIndex: 0,
  currentQuestion: null,
  answers: [],
  evaluationStartTime: null,
  questionStartTime: null,
  timeRemaining: 0,
  timer: null,
  isActive: false,
  isCompleted: false,
  loading: false,
  error: null,
  accessData: null,
  timeoutResult: null, // Store timeout result to trigger UI update
}

const getters = {
  isEvaluationActive: (state) => state.isActive,
  isEvaluationCompleted: (state) => state.isCompleted,
  getCurrentQuestion: (state) => state.currentQuestion,
  getCurrentQuestionIndex: (state) => state.currentQuestionIndex,
  getTotalQuestions: (state) => state.questions.length,
  getTimeRemaining: (state) => state.timeRemaining,
  getEvaluationProgress: (state) => {
    if (state.questions.length === 0) return 0
    // Show progress based on questions answered, not current question index
    return (state.answers.length / state.questions.length) * 100
  },

  getTimerProgress: (state) => {
    if (!state.currentQuestion || !state.currentQuestion.time_limit) return 0
    const timeLimit = state.currentQuestion.time_limit
    const timeElapsed = timeLimit - state.timeRemaining
    return Math.max(0, Math.min(100, (timeElapsed / timeLimit) * 100))
  },
  getAnswers: (state) => state.answers,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  hasMoreQuestions: (state) => state.currentQuestionIndex < state.questions.length,
  getAccessData: (state) => state.accessData,
  getEvaluationSummary: (state) => ({
    totalQuestions: state.questions.length,
    answeredQuestions: state.answers.length,
    currentIndex: state.currentQuestionIndex,
    startTime: state.evaluationStartTime,
    accessData: state.accessData,
  }),
  getTimeoutResult: (state) => state.timeoutResult,
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  SET_ACCESS_DATA(state, data) {
    state.accessData = data
  },

  START_EVALUATION(state, { questions, accessData }) {
    state.questions = questions
    state.accessData = accessData
    state.currentQuestionIndex = 0
    state.answers = []
    state.evaluationStartTime = new Date()
    state.isActive = true
    state.isCompleted = false
    state.error = null

    // Set first question
    if (questions.length > 0) {
      state.currentQuestion = questions[0]
      state.questionStartTime = new Date()
      state.timeRemaining = questions[0].time_limit || 60
    }
  },

  SET_CURRENT_QUESTION(state, question) {
    state.currentQuestion = question
    state.questionStartTime = new Date()
    state.timeRemaining = question.time_limit || 60
  },

  UPDATE_TIME_REMAINING(state, time) {
    state.timeRemaining = Math.max(0, time)
  },

  SET_TIMER(state, timer) {
    state.timer = timer
  },

  CLEAR_TIMER(state) {
    if (state.timer) {
      clearInterval(state.timer)
      state.timer = null
    }
  },

  NEXT_QUESTION(state) {
    state.currentQuestionIndex++

    if (state.currentQuestionIndex < state.questions.length) {
      const nextQuestion = state.questions[state.currentQuestionIndex]
      state.currentQuestion = nextQuestion
      state.questionStartTime = new Date()
      state.timeRemaining = nextQuestion.time_limit || 60
    } else {
      // No more questions - evaluation completed
      state.currentQuestion = null
      state.isActive = false
      state.isCompleted = true
    }
  },

  ADD_ANSWER(state, answerData) {
    const answer = {
      questionId: answerData.questionId,
      questionIndex: state.currentQuestionIndex,
      selectedOption: answerData.selectedOption,
      timeSpent: answerData.timeSpent,
      timestamp: new Date(),
      question: state.currentQuestion,
    }

    state.answers.push(answer)
  },

  COMPLETE_EVALUATION(state) {
    state.isActive = false
    state.isCompleted = true
    state.currentQuestion = null
  },

  SET_TIMEOUT_RESULT(state, result) {
    state.timeoutResult = result
  },

  CLEAR_TIMEOUT_RESULT(state) {
    state.timeoutResult = null
  },

  RESET_EVALUATION(state) {
    // Clear timer if it exists
    if (state.timer) {
      clearInterval(state.timer)
    }

    state.currentEvaluation = null
    state.questions = []
    state.currentQuestionIndex = 0
    state.currentQuestion = null
    state.answers = []
    state.evaluationStartTime = null
    state.questionStartTime = null
    state.timeRemaining = 0
    state.timer = null
    state.isActive = false
    state.isCompleted = false
    state.error = null
    state.accessData = null
    state.timeoutResult = null
  },
}

const actions = {
  async startEvaluation({ commit, dispatch }, { groupId, accessData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const groupResponse = await groupsApi.getGroup(groupId)
      const group = groupResponse.data || groupResponse

      if (!group || !group.school_grade_id) {
        throw new Error('No se pudo obtener el grado escolar del grupo')
      }

      const response = await questionService.getQuestionsBySchoolGrade(group.school_grade_id, accessData?.pin_code)

      if (!response || response.length === 0) {
        throw new Error('No hay preguntas disponibles para este grado escolar')
      }

      const questions = Array.isArray(response) ? response : response.data || []

      if (questions.length === 0) {
        throw new Error('No hay preguntas disponibles para este grado escolar')
      }

      // Start the evaluation
      commit('START_EVALUATION', { questions, accessData })

      // Start the timer for the first question
      dispatch('startQuestionTimer')

      return { success: true, data: { questions, firstQuestion: questions[0] } }
    } catch (error) {
      const errorMessage = error.message || 'Error al iniciar la evaluación'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  startQuestionTimer({ state, commit, dispatch }) {
    // Clear any existing timer
    commit('CLEAR_TIMER')

    const timer = setInterval(() => {
      const newTime = state.timeRemaining - 1
      commit('UPDATE_TIME_REMAINING', newTime)

      if (newTime <= 0) {
        // Time's up - move to next question automatically
        commit('CLEAR_TIMER')
        dispatch('handleTimeUp')
      }
    }, 1000)

    // Store timer reference in state
    commit('SET_TIMER', timer)
  },

  async submitAnswer({ state, commit, dispatch }, selectedOption) {
    if (!state.currentQuestion || !state.isActive)
      return { success: false, error: 'No active question' }

    const timeSpent = state.currentQuestion.time_limit - state.timeRemaining

    // Add answer to answers array
    commit('ADD_ANSWER', {
      questionId: state.currentQuestion.id,
      selectedOption,
      timeSpent,
    })

    // Clear current timer
    commit('CLEAR_TIMER')

    let answerResponse = null
    let selectedOptionLabel = null

    try {
      if (selectedOption !== null && selectedOption !== undefined) {
        if (state.currentQuestion.options && Array.isArray(state.currentQuestion.options)) {
          const option = state.currentQuestion.options.find((opt) => {
            return opt.id == selectedOption
          })
          selectedOptionLabel = option?.label
        } else if (state.currentQuestion.question_type === 'true_false') {
          selectedOptionLabel = selectedOption === 1 ? '1' : '0'
        } else {
          selectedOptionLabel = selectedOption
        }
      }

      answerResponse = await questionService.answerQuestion(state.currentQuestion.id, {
        selected_option: selectedOptionLabel,
        time_taken: timeSpent,
      })
    } catch (error) {
      console.warn('Failed to submit answer to API:', error)
      // Extract error message from backend (handles 422 and other errors)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit answer'
      return { success: false, error: errorMessage }
    }

    return {
      success: true,
      data: answerResponse,
      questionId: state.currentQuestion.id,
      timeSpent,
    }
  },

  async handleTimeUp({ state, commit }) {
    if (!state.currentQuestion || !state.isActive) return

    const timeSpent = state.currentQuestion.time_limit

    // Add empty answer (no option selected)
    commit('ADD_ANSWER', {
      questionId: state.currentQuestion.id,
      selectedOption: null, // No answer selected
      timeSpent,
    })

    // Submit timeout answer to backend
    let answerResponse = null
    try {
      answerResponse = await questionService.answerQuestion(state.currentQuestion.id, {
        selected_option: null, // Timeout - no option selected
        time_taken: timeSpent,
      })

      console.log('Timeout answer response:', answerResponse)
    } catch (error) {
      console.warn('Failed to submit timeout answer to API:', error)
      // Extract error message from backend (handles 422 and other errors)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit timeout answer'
      return { success: false, error: errorMessage }
    }

    // Store timeout result to trigger UI update
    const result = {
      success: true,
      data: answerResponse,
      questionId: state.currentQuestion.id,
      timeSpent,
      timeout: true, // Flag to indicate this was a timeout
    }

    commit('SET_TIMEOUT_RESULT', result)
    return result
  },

  moveToNextQuestion({ state, commit, dispatch, getters }) {
    if (!getters.hasMoreQuestions) {
      // No more questions - complete evaluation and finish session
      commit('COMPLETE_EVALUATION')
      dispatch('finishSessionAutomatically')
      return
    }

    // Move to next question
    commit('NEXT_QUESTION')

    // Start timer for new question
    if (state.isActive) {
      dispatch('startQuestionTimer')
    }
  },

  completeEvaluation({ commit }) {
    // Clear any active timer
    commit('CLEAR_TIMER')

    commit('COMPLETE_EVALUATION')
  },

  resetEvaluation({ commit }) {
    // Clear any active timer
    commit('CLEAR_TIMER')

    commit('RESET_EVALUATION')
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  // Automatic session finish when evaluation is completed
  async finishSessionAutomatically({ dispatch, rootGetters }) {
    try {
      // Get session ID from session store
      const sessionId = rootGetters['session/getSessionId']

      if (sessionId) {
        console.log('Automatically finishing session:', sessionId)
        await dispatch('session/finishSession', null, { root: true })
      }
    } catch (error) {
      console.warn('Failed to automatically finish session:', error)
      // Don't block the evaluation completion if session finish fails
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
